/** Mattoni del mockup v2: severità, stati, flag, tag di proposta, KPI, intestazione di pagina, barra del processo. */
import { Alert, Button, Card, Col, Empty, Row, Skeleton, Tag } from "antd";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { UseQueryResult } from "@tanstack/react-query";
import type {
  FlagSettimana,
  OwnerDto,
  Severita,
  StatoClaim,
  StatoProposta,
  StatoSegnalazione,
  TipoClassificazione,
  TipoNc,
  TipoProposta,
} from "@qa/shared";
import { messaggioErrore } from "../i18n/errori";
import { palette } from "../theme";
import { fmtDataOra } from "../lib/format";

export function Sev({ s }: { s: Severita }) {
  return <span className={`sev ${s}`}>{s}</span>;
}

export function Urgente({ lungo }: { lungo?: boolean }) {
  const { t } = useTranslation();
  return <span className="urg">{lungo ? t("redflag.urgenteLungo") : t("redflag.urgente")}</span>;
}

export function Flag({ f }: { f: FlagSettimana | null | undefined }) {
  const { t } = useTranslation();
  if (!f) return null;
  return <span className={f === "NEW" ? "flag-new" : "flag-upd"}>{t(`redflag.flag.${f}`)}</span>;
}

const COLORE_STATO_RF: Record<StatoSegnalazione, string> = { nuova: "warning", in_gestione: "processing", da_aggiornare: "error", chiusa: "success", persa: "default" };
export function StatoRf({ s }: { s: StatoSegnalazione }) {
  const { t } = useTranslation();
  return (
    <Tag color={COLORE_STATO_RF[s]} bordered={false} style={{ marginInlineEnd: 0 }}>
      {t(`dominio.statoRf.${s}`)}
    </Tag>
  );
}

const COLORE_TIPO: Record<TipoClassificazione, { bg: string; fg: string }> = {
  nc_fornitore: { bg: palette.primarySoft, fg: palette.primary },
  nc_interna: { bg: palette.warnSoft, fg: palette.warn },
  commerciale: { bg: palette.sunk, fg: palette.muted },
  capacita: { bg: palette.sunk, fg: palette.muted },
  da_classificare: { bg: palette.badSoft, fg: palette.bad },
};
export function TipoTag({ tipo }: { tipo: TipoClassificazione }) {
  const { t } = useTranslation();
  const c = COLORE_TIPO[tipo];
  return (
    <Tag bordered={false} style={{ background: c.bg, color: c.fg, marginInlineEnd: 4 }}>
      {t(`dominio.tipoClass.${tipo}`)}
    </Tag>
  );
}
export function TipoNcTag({ tipo }: { tipo: TipoNc }) {
  return <TipoTag tipo={tipo === "fornitore" ? "nc_fornitore" : "nc_interna"} />;
}

const COLORE_CLAIM: Record<StatoClaim, string> = { aperto: "warning", notificato: "processing", in_trattativa: "processing", accettato: "success", contestato: "error", recuperato: "success" };
export function StatoClaimTag({ s }: { s: StatoClaim }) {
  const { t } = useTranslation();
  return (
    <Tag color={COLORE_CLAIM[s]} bordered={false} style={{ marginInlineEnd: 0 }}>
      {t(`dominio.statoClaim.${s}`)}
    </Tag>
  );
}

const COLORE_PROPOSTA: Record<StatoProposta, string> = { da_approvare: "warning", approvata: "success", modificata: "success", rifiutata: "default" };
export function StatoPropostaTag({ s }: { s: StatoProposta }) {
  const { t } = useTranslation();
  return (
    <Tag color={COLORE_PROPOSTA[s]} bordered={false} style={{ marginInlineEnd: 0 }}>
      {t(`dominio.statoProposta.${s}`)}
    </Tag>
  );
}
const COLORE_TIPO_PROPOSTA: Record<TipoProposta, string> = { classificazione: "blue", collegamento_claim: "gold", nuova_voce: "default" };
export function TipoPropostaTag({ tipo }: { tipo: TipoProposta }) {
  const { t } = useTranslation();
  return (
    <Tag color={COLORE_TIPO_PROPOSTA[tipo]} bordered={false}>
      {t(`dominio.tipoProposta.${tipo}`)}
    </Tag>
  );
}

/** Tratteggio giallo: qualcosa di proposto e non ancora approvato. */
export const Prop = ({ children }: { children: ReactNode }) => <span className="prop">{children}</span>;
/** Grigio: dato in sola lettura da un altro sistema. */
export const Ro = ({ children }: { children: ReactNode }) => <span className="ro">{children}</span>;
/** Rosso corsivo: il dato che manca (il buco di oggi). */
export const Miss = ({ children }: { children: ReactNode }) => <span className="miss">{children}</span>;

export function Owner({ o }: { o: OwnerDto | null | undefined }) {
  const { t } = useTranslation();
  return o?.nome ? <>{o.nome}</> : <Miss>{t("comune.daAssegnare")}</Miss>;
}

/** `sensibile`: valore e nota sono importi (valore a rischio, claim, costo della non qualità): oscurati nelle segnalazioni. */
export function Kpi({ label, value, note, color, extra, sensibile }: { label: ReactNode; value: ReactNode; note?: ReactNode; color?: string; extra?: ReactNode; sensibile?: boolean }) {
  const s = sensibile ? { "data-sensitive": "" } : {};
  return (
    <Card size="small" styles={{ body: { padding: "12px 14px" } }} style={{ height: "100%" }}>
      <div className="kpi-l">{label}</div>
      <div className="kpi-v" style={color ? { color } : undefined} {...s}>
        {value}
      </div>
      {note !== undefined && (
        <div className="kpi-d" {...s}>
          {note}
        </div>
      )}
      {extra}
    </Card>
  );
}
export const KpiRow = ({ children }: { children: ReactNode }) => <div className="rs-kpi">{children}</div>;

export interface Crumb {
  label: ReactNode;
  to?: string;
}
export function PageHead({ title, sub, crumbs, extra }: { title: ReactNode; sub?: ReactNode; crumbs?: Crumb[]; extra?: ReactNode }) {
  return (
    <>
      {crumbs && crumbs.length > 0 && (
        <nav className="crumbs">
          {crumbs.map((c, i) => (
            <span key={i}>
              {i > 0 && " › "}
              {c.to ? <Link to={c.to}>{c.label}</Link> : c.label}
            </span>
          ))}
        </nav>
      )}
      <div className="pagehead">
        <div style={{ minWidth: 0 }}>
          <h1>{title}</h1>
          {sub && <p>{sub}</p>}
        </div>
        {extra && <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>{extra}</div>}
      </div>
    </>
  );
}

/** Barra del processo: passi con stato fatto / corrente / da fare. */
export function Processo({ passi }: { passi: { titolo: ReactNode; chi?: ReactNode; stato: "done" | "cur" | "" }[] }) {
  return (
    <div className="flowline" role="list">
      {passi.map((p, i) => (
        <div key={i} className={`st ${p.stato}`} role="listitem" aria-current={p.stato === "cur" ? "step" : undefined}>
          <b>{p.titolo}</b>
          {p.chi}
        </div>
      ))}
    </div>
  );
}

/** Stato di caricamento o errore di una query; i figli solo con i dati. */
export function Stato<T>({ q, children, righe = 6 }: { q: UseQueryResult<T>; children: (d: T) => ReactNode; righe?: number }) {
  const { t } = useTranslation();
  if (q.isLoading) return <Skeleton active paragraph={{ rows: righe }} />;
  if (q.isError)
    return (
      <Alert
        type="error"
        showIcon
        message={t("errore.caricamento")}
        description={messaggioErrore(q.error)}
        action={
          <Button size="small" onClick={() => void q.refetch()}>
            {t("azioni.ricarica")}
          </Button>
        }
      />
    );
  if (q.data === undefined) return null;
  return <>{children(q.data)}</>;
}

/** Storico (aggiornamenti di segnalazione e claim): quando e chi in piccolo, il testo sotto. Regge anche a 390 px. */
export function Storico({ righe, vuoto }: { righe: { id: string; at: string; autore: string; testo: string }[]; vuoto: ReactNode }) {
  if (!righe.length) return <Vuoto titolo={vuoto} />;
  return (
    <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
      {righe.map((a) => (
        <li key={a.id} style={{ padding: "8px 0", borderBottom: `1px solid ${palette.hair}` }}>
          <div className="muted small">
            <span className="num">{fmtDataOra(a.at)}</span> · {a.autore}
          </div>
          <div style={{ whiteSpace: "pre-wrap" }}>{a.testo}</div>
        </li>
      ))}
    </ol>
  );
}

export function Vuoto({ titolo, testo }: { titolo: ReactNode; testo?: ReactNode }) {
  return (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <>
          <b style={{ display: "block", color: palette.fg }}>{titolo}</b>
          {testo}
        </>
      }
    />
  );
}

export function Griglia({ children, side, gutter = 16 }: { children: [ReactNode, ReactNode]; side?: number; gutter?: number }) {
  return (
    <Row gutter={[gutter, gutter]}>
      <Col xs={24} lg={side ? 24 - side : 12}>
        {children[0]}
      </Col>
      <Col xs={24} lg={side ?? 12}>
        {children[1]}
      </Col>
    </Row>
  );
}
